<template>
  <div id="editOrganizationPromptModal" class="modal fade" tabindex="-1">
    <div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ isEditMode ? 'Editing' : 'Viewing' }} Organization Prompt</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" :disabled="saving || deactivating"></button>
        </div>
        <div class="modal-body">
          <div v-if="saving || deactivating" class="d-flex flex-column align-items-center justify-content-center p-5" style="min-height: 200px;">
            <div class="spinner-border text-primary mb-3" role="status">
              <span class="visually-hidden">{{ saving ? 'Creating...' : 'Deactivating...' }}</span>
            </div>
            <div class="text-muted">{{ saving ? 'Creating new version...' : 'Deactivating version...' }}</div>
          </div>
          
          <div v-else-if="prompt">
            <div class="mb-3">
              <div class="text-muted small">
                {{ isEditMode ? 'Editing' : 'Viewing' }} version <strong>{{ prompt.version }}</strong>
              </div>
            </div>
            
            <div class="mb-3">
              <label class="form-label fw-bold">Customer</label>
              <div class="text-muted">{{ getCustomerName(prompt.customerId) }}</div>
            </div>
            
            <div class="mb-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <label class="form-label fw-bold mb-0">Prompt Content</label>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  title="Copy prompt content"
                  @click="copyContent"
                >
                  <font-awesome-icon :icon="faCopy" />
                </button>
              </div>
              <textarea
                class="form-control"
                v-model="promptContent"
                rows="15"
                placeholder="Enter the organization prompt content..."
                :disabled="saving || deactivating || !isEditMode"
                :readonly="!isEditMode"
              ></textarea>
              <div v-if="isEditMode" class="form-text">
                Make your edits and save to create a new version. The new version will automatically become active.
              </div>
            </div>

            <div v-if="errorMessage" class="alert alert-danger" role="alert">
              {{ errorMessage }}
            </div>
          </div>
        </div>
        <div class="modal-footer" v-if="!saving && !deactivating && prompt">
          <!-- View Mode Buttons -->
          <template v-if="!isEditMode">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            <button type="button" class="btn btn-outline-secondary" @click="enterEditMode">Edit</button>
            <button 
              v-if="prompt.isActive" 
              type="button" 
              class="btn btn-outline-danger" 
              @click="onDeactivate"
            >
              Deactivate
            </button>
            <button 
              v-if="!prompt.isActive" 
              type="button" 
              class="btn btn-outline-warning" 
              @click="onSetActive"
            >
              Set Active
            </button>
          </template>
          
          <!-- Edit Mode Buttons -->
          <template v-else>
            <button type="button" class="btn btn-secondary" @click="exitEditMode">Cancel</button>
            <button type="button" class="btn btn-primary" @click="onReview" :disabled="!canSubmit">
              Review
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import InsightsService from '../services/InsightsService.js';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';

export default {
  name: 'EditOrganizationPromptModal',
  components: { FontAwesomeIcon },
  props: {
    show: Boolean,
    prompt: {
      type: Object,
      default: null
    },
    returnFromReviewDraft: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      promptContent: '',
      originalContent: '',
      saving: false,
      deactivating: false,
      errorMessage: '',
      isEditMode: false,
      insightsService: new InsightsService(),
      model: streetmetrics.model,
      currentModal: null,
      modalElement: null,
      _skipNextLoadPrompt: false,
      faCopy,
    };
  },
  computed: {
    canSubmit() {
      return this.promptContent.trim().length > 1;
    }
  },
  watch: {
    show(val) {
      if (val) {
        this.openModal();
        this.$nextTick(() => {
          if (this.returnFromReviewDraft) {
            this.promptContent = this.returnFromReviewDraft.promptContent ?? '';
            this.originalContent = this.returnFromReviewDraft.previousContent ?? '';
            this.isEditMode = true;
            this._skipNextLoadPrompt = true;
            this.$emit('appliedReturnDraft');
          } else {
            this.loadPromptContent();
          }
        });
      } else {
        this.closeModal();
        this.resetForm();
      }
    },
    prompt: {
      handler(val) {
        if (val && this.show) {
          this.loadPromptContent();
        }
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    this.modalElement = document.getElementById('editOrganizationPromptModal');
    this.currentModal = new bootstrap.Modal(this.modalElement);
    if (this.show) {
      this.currentModal.show();
      this.$nextTick(() => {
        if (this.returnFromReviewDraft) {
          this.promptContent = this.returnFromReviewDraft.promptContent ?? '';
          this.originalContent = this.returnFromReviewDraft.previousContent ?? '';
          this.isEditMode = true;
          this._skipNextLoadPrompt = true;
          this.$emit('appliedReturnDraft');
        } else {
          this.loadPromptContent();
        }
      });
    }
    this.modalElement.addEventListener('hidden.bs.modal', () => {
      // Clean up backdrop if it still exists
      const backdrop = document.querySelector('.modal-backdrop');
      if (backdrop) {
        backdrop.remove();
      }
      // Clean up body styles
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      
      this.$emit('close');
    });
  },
  beforeDestroy() {
    if (this.modalElement) {
      this.modalElement.removeEventListener('hidden.bs.modal');
    }
  },
  methods: {
    openModal() {
      if (this.currentModal) {
        this.currentModal.show();
      }
    },
    closeModal() {
      if (this.currentModal) {
        this.currentModal.hide();
      }
    },
    resetForm() {
      this.promptContent = '';
      this.originalContent = '';
      this.errorMessage = '';
      this.saving = false;
      this.deactivating = false;
      this.isEditMode = false;
    },
    loadPromptContent() {
      if (this._skipNextLoadPrompt) {
        this._skipNextLoadPrompt = false;
        return;
      }
      if (this.prompt) {
        if (this.prompt.promptContent) {
          // Use the promptContent that was passed in (already fetched in parent)
          this.promptContent = this.prompt.promptContent;
          this.originalContent = this.prompt.promptContent;
        } else {
          // If content not available, fetch it
          this.fetchPromptContent();
        }
      }
    },
    async fetchPromptContent() {
      if (!this.prompt || !this.prompt.customerId || !this.prompt.promptId) return;
      
      try {
        const history = await this.insightsService.getOrganizationPromptHistory(this.prompt.customerId);
        const fullPrompt = history.find(p => p.promptId === this.prompt.promptId);
        if (fullPrompt && fullPrompt.promptContent) {
          this.promptContent = fullPrompt.promptContent;
          this.originalContent = fullPrompt.promptContent;
        } else {
          this.errorMessage = 'Could not load prompt content. Please try again.';
        }
      } catch (error) {
        console.error('Error fetching prompt content:', error);
        this.errorMessage = 'Error loading prompt content. Please try again.';
      }
    },
    getCustomerName(customerId) {
      const customer = this.model.customers?.find(c => c.customerId === customerId);
      return customer?.customerName || `Customer ${customerId}`;
    },
    async copyContent() {
      const text = this.promptContent ?? '';
      if (!text.trim()) {
        this.model.toast.message = null;
        this.$nextTick(() => {
          this.model.toast.message = 'No prompt content to copy';
          this.model.toast.background = 'bg-warning';
        });
        return;
      }
      try {
        await navigator.clipboard.writeText(text);
        this.model.toast.message = null;
        this.$nextTick(() => {
          this.model.toast.message = 'Prompt content copied to clipboard!';
          this.model.toast.background = 'bg-success';
        });
      } catch (error) {
        this.model.toast.message = null;
        this.$nextTick(() => {
          this.model.toast.message = 'Failed to copy prompt content';
          this.model.toast.background = 'bg-danger';
        });
      }
    },
    onReview() {
      if (!this.canSubmit || !this.prompt) return;
      this.$emit('review', {
        customerId: this.prompt.customerId,
        promptContent: this.promptContent.trim(),
        customerName: this.getCustomerName(this.prompt.customerId),
        previousContent: this.originalContent || ''
      });
    },
    enterEditMode() {
      this.isEditMode = true;
      this.originalContent = this.promptContent;
    },
    exitEditMode() {
      this.isEditMode = false;
      this.promptContent = this.originalContent;
      this.errorMessage = '';
    },
    onSetActive() {
      this.$emit('setActive', this.prompt);
    },
    onDeactivate() {
      this.$emit('deactivate', this.prompt);
    }
  }
};
</script>

<style scoped>
.form-label {
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}

textarea.form-control {
  font-family: monospace;
  font-size: 0.9rem;
}
</style>
